import React, { useCallback, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import { BOOKINGS_QUERY } from './query'
import Error from 'components/error'
import { DateTime } from 'luxon'
import { onReschedule, onPayment, onCancellation } from 'lib/utils'
import { RefetchContext } from 'lib/utils/refetchContext'
import {
  bookingsForTodaySchedule,
  bookingsUpcoming,
} from 'pages/pga-coach/bookings/schedule/bookings-list-helpers'
import TodaysSchedule from './todays-schedule'
import BusinessAtAGlance from './business-at-a-glance'
import QuickActions from './quick-actions'
import ManageBusiness from './manage-business'
import MarketplaceListingBanner from './marketplace-listing-banner'
import HolidayLessonPackBanner from './holiday-lesson-pack-banner'
import useStyles from './styles'

const UPCOMING_LESSONS_LIMIT = 4

const DefaultDashboard = ({
  hasOffering,
  marketplaceListingReasons = [],
  upgradeFlow,
  isGroupEventsLimitReached,
}) => {
  const classes = useStyles()

  const todayVariables = useMemo(
    () => ({
      startDateTime: {
        from: DateTime.local()
          .startOf('day')
          .toISO(),
        to: DateTime.local()
          .endOf('day')
          .toISO(),
      },
      orderBy: [{ field: 'START_DATE_TIME', direction: 'ASC' }],
    }),
    [],
  )

  const upcomingVariables = useMemo(
    () => ({
      startDateTime: {
        from: DateTime.local().toISO(),
        to: null,
      },
      orderBy: [{ field: 'START_DATE_TIME', direction: 'ASC' }],
      limit: UPCOMING_LESSONS_LIMIT,
    }),
    [],
  )

  const {
    data: todayData,
    loading: todayLoading,
    error: todayError,
    refetch: refetchTodaySchedule,
  } = useQuery(BOOKINGS_QUERY, { variables: todayVariables })

  const {
    data: upcomingData,
    loading: upcomingLoading,
    error: upcomingError,
    refetch: refetchUpcomingLessons,
  } = useQuery(BOOKINGS_QUERY, { variables: upcomingVariables })

  const refreshBookings = useCallback(
    () => Promise.all([refetchTodaySchedule(), refetchUpcomingLessons()]),
    [refetchTodaySchedule, refetchUpcomingLessons],
  )

  if (todayError || upcomingError) return <Error />

  const schedule = bookingsForTodaySchedule(
    todayData?.currentUser?.schedule || [],
  )
  const upcomingLessons = bookingsUpcoming(
    upcomingData?.currentUser?.schedule || [],
    { limit: UPCOMING_LESSONS_LIMIT },
  )
  const isScheduleLoading =
    (todayLoading && !todayData) || (upcomingLoading && !upcomingData)

  return (
    <Box className={classes.mainContent}>
      <MarketplaceListingBanner
        reasons={marketplaceListingReasons}
        upgradeFlow={upgradeFlow}
      />
      <HolidayLessonPackBanner />
      <RefetchContext.Provider value={refreshBookings}>
        <BusinessAtAGlance />
        <Box className={classes.scheduleRow}>
          <TodaysSchedule
            schedule={schedule}
            loading={isScheduleLoading}
            hasOffering={hasOffering}
            onReschedule={() => onReschedule(refreshBookings)}
            onPayment={() => onPayment(refreshBookings)}
            onCancellation={() => onCancellation(refreshBookings)}
          />
          <QuickActions
            hasOffering={hasOffering}
            isGroupEventsLimitReached={isGroupEventsLimitReached}
          />
        </Box>
        <ManageBusiness
          upcomingLessons={upcomingLessons}
          lessonsLoading={isScheduleLoading}
          hasOffering={hasOffering}
        />
      </RefetchContext.Provider>
    </Box>
  )
}

export default DefaultDashboard
